/**
 * A county selector component integrated with react-final-form.
 *
 * This component displays a dropdown of counties based on a selected state.
 * It has several features like automatically clearing the value when the state changes,
 * auto-selecting a county if there's only one option, and updating other
 * form fields based on the county selection.
 *
 * @param {object} props - The component's props.
 *
 * // --- Standard react-final-form props ---
 * @param {object} props.input - Provided by react-final-form's <Field>.
 * @param {object} props.meta - Provided by react-final-form's <Field>.
 * @param {object} props.formApi - The final-form instance.
 * @param {object} props.formValues - Current values of the form.
 *
 * // --- Custom props ---
 * @param {object} props.counties - An object where keys are state abbreviations
 *   (e.g., "CA") and values are arrays of county objects.
 *
 * @param {object} props.schemaProps - Configuration for the component's behavior.
 * @param {string} props.schemaProps.machine_name - (Required) The unique name/ID for the input field.
 * @param {string|string[]|function} props.schemaProps.disableWhenField - The name of the field containing the state value. Used to filter counties. Also used to determine if this field should be disabled.
 * @param {string} [props.schemaProps.stateField] - Alternative to disableWhenField for specifying the state field.
 * @param {string} [props.schemaProps.parent_machine_name] - A prefix for field names in nested forms.
 * @param {string} [props.schemaProps.updateBls] - The `machine_name` of a field to update with the selected county's `bls` property.
 * @param {string} [props.schemaProps.updateBlsCode] - The `machine_name` of a field to update with the selected county's `blsAreaCode`.
 * @param {(boolean|function)} [props.schemaProps.restrictCountyOptions] - If true or returns true, filters counties based on the `updateBls` field's value.
 * @param {object} [props.schemaProps.naOptionWhen] - An object like `{ when: 'fieldName', is: 'value' }` to force the "N/A" option.
 * @param {function} [props.schemaProps.countySource] - A function that returns a `counties` object, as an alternative to the `counties` prop. It receives `(formApi, counties)` as arguments.
 */
import React, { useState, useEffect } from "react";
import Select from "react-select";
import reactSelectStyle from "./reactSelectStyle";

const naOption = {
  bls: "N/A",
  blsAreaCode: 0,
  blsAreaId: 0,
  label: "N/A",
  value: 0,
};

const CountyselectInputBls = (props) => {
  const {
    input,
    meta,
    formApi,
    formValues,
    schemaProps: {
      machine_name,
      disableWhenField,
      parent_machine_name,
      updateBls,
      updateBlsCode,
      restrictCountyOptions,
      stateField,
      naOptionWhen,
      countySource,
    },
    counties,
    ...rest
  } = props;

  // --- DERIVED VALUES (from props, no local state needed for these) ---
  const allCounties =
    typeof countySource === "function"
      ? countySource(formApi, counties)
      : counties;
  let disableField =
    rest && rest.tableName
      ? rest.tableName + disableWhenField
      : disableWhenField;

  if (Array.isArray(disableWhenField)) {
    disableField = disableWhenField[0].when;
  }

  let lookup_state, lookup_state_addendum;

  if (
    formApi &&
    formApi.getFieldState(disableField) &&
    formApi.getFieldState(disableField).value !== undefined
  ) {
    lookup_state_addendum = formApi.getFieldState(disableField).value;
  }

  lookup_state = parent_machine_name
    ? formValues?.[parent_machine_name]?.[0]?.[disableField]
    : formValues?.[disableField];

  if (!!stateField) {
    lookup_state = formValues[stateField];
  }

  const [stateValue, setStateValue] = useState(lookup_state);

  useEffect(() => {
    setStateValue(lookup_state || lookup_state_addendum);
  }, [lookup_state, lookup_state_addendum]);

  const machine_name_modified = parent_machine_name
    ? `${parent_machine_name}[0]${machine_name}`
    : machine_name;
  const shouldRestrict =
    typeof restrictCountyOptions === "function"
      ? restrictCountyOptions(formApi)
      : restrictCountyOptions;
  const chosenMSATitle = formValues[updateBls];

  // --- SIDE EFFECTS ---
  useEffect(() => {
    // This single effect controls all automatic changes to the county field's value.

    // Case 1: Set to N/A if condition is met. This takes highest precedence.
    if (!!naOptionWhen && formValues[naOptionWhen.when] === naOptionWhen.is) {
      if (!input.value || input.value.value !== naOption.value) {
        input.onChange(naOption);
      }
      return; // Exit early
    }

    // Case 2: Clear the value if it's no longer valid for the currently selected state.
    const validOptionsForState = allCounties ? allCounties[stateValue] : [];
    if (input.value && validOptionsForState) {
      const valueIsInvalid = !validOptionsForState.find(
        (option) => option.value === input.value.value
      );

      if (valueIsInvalid) {
        input.onChange(undefined);
        if (updateBls && !shouldRestrict) {
          const blsMachineName = parent_machine_name
            ? `${parent_machine_name}[0]${updateBls}`
            : updateBls;
          formApi.change(blsMachineName, undefined);
        }
        if (updateBlsCode && !shouldRestrict) {
          formApi.change(updateBlsCode, undefined);
        }
        return; // Exit: we just cleared the value, so no other effects should run.
      }
    }

    // Case 3: Auto-select county if restriction results in a single option.
    if (shouldRestrict && chosenMSATitle && stateValue) {
      const restrictedOptions =
        allCounties[stateValue]?.filter((c) => c.bls === chosenMSATitle) ?? [];
      if (restrictedOptions.length === 1) {
        if (!input.value || input.value.value !== restrictedOptions[0].value) {
          input.onChange(restrictedOptions[0]);
        }
      }
    }
  }, [
    stateValue,
    chosenMSATitle,
    shouldRestrict,
    allCounties,
    input.value,
    input.onChange,
    formValues,
    naOptionWhen,
    updateBls,
    updateBlsCode,
    formApi,
    machine_name_modified,
    parent_machine_name,
  ]);

  // --- RENDER LOGIC ---
  let options = allCounties ? allCounties[stateValue] : [];
  if (shouldRestrict && chosenMSATitle) {
    options =
      allCounties[stateValue]?.filter((c) => c.bls === chosenMSATitle) ?? [];
  }
  if (!!naOptionWhen && formValues[naOptionWhen.when] === naOptionWhen.is) {
    options = [naOption];
  }

  if (options && options.length > 0) {
    options.sort(function (a, b) {
      if (a.label < b.label) return -1;
      if (a.label > b.label) return 1;
      return 0;
    });
  }

  const oneCounty = shouldRestrict && options?.length === 1;
  let isDisabled = oneCounty;
  if (typeof disableWhenField === "function") {
    isDisabled = isDisabled || disableWhenField(formApi.getState().values);
  }

  const handleChange = (selectedOption) => {
    input.onChange(selectedOption);

    if (updateBls && selectedOption?.bls) {
      const blsMachineName = parent_machine_name
        ? `${parent_machine_name}[0]${updateBls}`
        : updateBls;
      formApi.change(blsMachineName, selectedOption.bls);
    }
    if (updateBlsCode && selectedOption?.blsAreaCode) {
      formApi.change(updateBlsCode, selectedOption.blsAreaCode);
    }
  };

  return (
    <div className="county-select-input">
      <Select
        {...input}
        id={machine_name}
        value={input.value}
        isSearchable={true}
        backspaceRemovesValue={true}
        placeholder="Type search term here"
        hideSelectedOptions={true}
        styles={reactSelectStyle}
        inputId={input.name}
        isDisabled={isDisabled}
        options={options || []}
        onChange={handleChange}
        getOptionLabel={(option) => option.label}
        getOptionValue={(option) => option.value}
      />
      {meta.error && meta.touched && (
        <span className="error-branch">{meta.error}</span>
      )}
    </div>
  );
};

export default CountyselectInputBls;
